import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { ehandoverAPI } from '../../../../../apis';
import { i18n } from '../../../../../language';
import { getArrayRandomKey } from '../../../../../utils';

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#fff',
    padding: '1.875rem',
    borderRadius: '0 0.93rem 0.93rem 0.93rem',
  },
  tableContainer: {
    height: '65vh',
  },
  table: {
    position: 'relative',
    minWidth: '45rem',
    '& .MuiTableCell-root': {
      border: 'none',
      fontSize: '1.125rem',
      fontWeight: '500',
      lineHeight: '1.675rem',
      color: '#2e4469',
      padding: '0 1rem ',
    },
    '& .MuiTableHead-root': {
      position: 'sticky',
      top: '0',
      left: '0',
      zIndex: '10',
      '& .MuiTableRow-root': {
        backgroundColor: '#fff',
        textTransform: 'uppercase',
      },
      '& .MuiTableCell-root': {
        fontWeight: 'bold',
        color: '#538fff',
        padding: '1rem',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
      },
      '& .MuiTableCell-root:nth-child(1)': {
        paddingLeft: '1.313rem',
      },
      '& .MuiTableCell-root:nth-last-child(1)': {
        paddingRight: '1.883rem',
      },
    },
    '& .MuiTableBody-root': {
      '& :hover': {
        backgroundColor: '#f5faff',
      },
      '& > .MuiTableRow-root': {
        borderBottom: '1px solid #E6E8E9',
      },
      '& .MuiTableCell-root': {
        height: '3.5rem',
        fontFamily: 'NotoSans-Medium',
      },
      '& .MuiTableCell-root:nth-child(1)': {
        paddingLeft: '1.313rem',
      },
      '& .MuiTableCell-root:nth-last-child(1)': {
        paddingRight: '1.883rem',
      },
    },
  },
  tableHead: {
    whiteSpace: 'nowrap',
  },
  add: {
    width: '5.688rem',
    height: '3rem',
    background: '#538fff',
    color: '#fff',
    borderRadius: '0.625rem',
    fontWeight: '500',
    fontFamily: 'NotoSans-Medium',
  },
  checkBox: {
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
  },
}));

export default function TwoWayCommunication() {
  const classes = useStyles();
  const [twoWayCommList, setTwoWayCommList] = useState([]);
  const [twoWayEnable, setTwoWayEnable] = useState(false);
  const loginInfo = useSelector((state) => state.loginInfo);
  const language = useSelector((state) => state.settings.language);
  const tableRef = useRef();

  const getSettingTwoWayCommList = useCallback(async () => {
    try {
      const { result } = await ehandoverAPI.getSettingTwoWayComm({});
      const newList = [
        {
          text: i18n.t('EHANDOVER_DEFAULT_GROUPS'),
          createNote: result?.TWO_WAY_COMM_DEFAULT_GRP_CREATE_NOTE_YN === 'Y',
          replyNote: result?.TWO_WAY_COMM_DEFAULT_GRP_REPLY_NOTE_YN === 'Y',
          type: 'DEFAULT_GROUPS',
        },
        {
          text: i18n.t('EHANDOVER_INDIVIDUAL'),
          createNote: result?.TWO_WAY_COMM_INDIVIDUAL_CREATE_NOTE_YN === 'Y',
          replyNote: result?.TWO_WAY_COMM_INDIVIDUAL_REPLY_NOTE_YN === 'Y',
          type: 'INDIVIDUAL',
        },
      ];
      setTwoWayEnable(result?.TWO_WAY_COMM_ENABLE_YN === 'Y');
      setTwoWayCommList(newList);
    } catch (error) {}
  }, []);

  const updateTwoWayCommList = async (rowItem, isChecked, noteType) => {
    try {
      const newTwoWayCommList = _.map(twoWayCommList, (item) => ({
        ...item,
        [noteType]: item.type === rowItem.type ? isChecked : item[noteType],
      }));
      let params = {
        defaultGrpCreateNoteYn: newTwoWayCommList?.[0]?.createNote ? 'Y' : 'N',
        defaultGrpReplyNoteYn: newTwoWayCommList?.[0]?.replyNote ? 'Y' : 'N',
        individualCreateNoteYn: newTwoWayCommList?.[1]?.createNote ? 'Y' : 'N',
        individualReplyNoteYn: newTwoWayCommList?.[1]?.replyNote ? 'Y' : 'N',
        twoWayCommEnableYn: twoWayEnable ? 'Y' : 'N',
      };
      await ehandoverAPI.updateSettingTwoWayComm(params);
      await getSettingTwoWayCommList();
    } catch (error) {}
  };

  const updateTwoWayEnableYn = async (isChecked) => {
    let params = {
      defaultGrpCreateNoteYn: twoWayCommList?.[0]?.createNote ? 'Y' : 'N',
      defaultGrpReplyNoteYn: twoWayCommList?.[0]?.replyNote ? 'Y' : 'N',
      individualCreateNoteYn: twoWayCommList?.[1]?.createNote ? 'Y' : 'N',
      individualReplyNoteYn: twoWayCommList?.[1]?.replyNote ? 'Y' : 'N',
      twoWayCommEnableYn: isChecked ? 'Y' : 'N',
    };
    await ehandoverAPI.updateSettingTwoWayComm(params);
    await getSettingTwoWayCommList();
  };

  useEffect(() => {
    getSettingTwoWayCommList();
  }, [language,loginInfo?.wardId]);

  return (
    <div className={classes.root}>
      <TableContainer className={classes.tableContainer} ref={tableRef}>
        <Box paddingLeft="1.313rem">
          <FormControlLabel
            id="ehandover_setting_enable_2_way_communication_checkbox"
            control={<Checkbox />}
            label={i18n.t('EHANDOVER_ENABLE_2_WAY_COMMUNICATION')}
            className={classes.checkBox}
            checked={twoWayEnable}
            onChange={(event) => updateTwoWayEnableYn(event?.target?.checked)}
          />
        </Box>
        <Table className={classes.table} aria-label="simple table">
          <TableHead className={classes.tableHead} id="bed_setting_table_head">
            <TableRow>
              <TableCell align="left" style={{ wordBreak: 'break-all' }}>
                {i18n.t('EHANDOVER_TYPE')}
              </TableCell>
              <TableCell align="center">{i18n.t('EHANDOVER_REPLY_NOTE')}</TableCell>
              <TableCell align="center">{i18n.t('EHANDOVER_CREATE_NOTE')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody id="bed_setting_table_body">
            {twoWayCommList.map((row, rowIndex) => {
              const { text, replyNote, createNote } = row;
              return (
                <TableRow
                  key={getArrayRandomKey(rowIndex, 'ehandover-access-control')}
                  id={`e_handover_setting_table_row${rowIndex}`}
                >
                  <TableCell align="left">{text}</TableCell>
                  <TableCell align="center">
                    <FormControlLabel
                      id="edit_access_control_checkbox"
                      control={<Checkbox />}
                      label=""
                      className={classes.checkBox}
                      checked={replyNote}
                      onChange={(event) => updateTwoWayCommList(row, event.target.checked, 'replyNote')}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <FormControlLabel
                      id="edit_access_control_checkbox"
                      control={<Checkbox />}
                      label=""
                      className={classes.checkBox}
                      checked={createNote}
                      onChange={(event) => updateTwoWayCommList(row, event.target.checked, 'createNote')}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
